// frontend/src/utils/gridTooltip.js
//
// The AG Grid half of the app's one hint shape: above the element, with an
// arrow pointing at it, after the same wait as an MUI Tooltip (theme.js
// MuiTooltip). App.jsx hands GRID_TOOLTIP_OPTIONS to provideGlobalGridOptions,
// so every grid gets it without asking; tooltips.test.js keeps any grid from
// setting these options itself.
//
// AG Grid has no placement option: it puts a tooltip 18px under the pointer
// (ag-stack BaseTooltipStateManager.positionTooltip). It does call
// `postProcessPopup` after every placement, its ResizeObserver re-placement
// included, so placeGridTooltip moves the card from there. The arrow is drawn by
// styles/grid-page.css from the `data-tooltip-placement` and
// `--tooltip-arrow-x` set here.

import { TOOLTIP_SHOW_DELAY_MS } from '../theme';

// Card edge to cell edge; the arrow fills it.
const GAP_PX = 8;
// The nearest a card comes to the window edge.
const EDGE_PX = 8;
// Keeps the arrow clear of the card's rounded corners.
const ARROW_INSET_PX = 12;
// What a grid hint belongs to: the whole cell or header, not the span under
// the pointer. A hint elsewhere (a menu item, a rich-select row) belongs to
// the element itself.
const ANCHOR = '.ag-cell, .ag-header-cell, .ag-header-group-cell';

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

/**
 * `postProcessPopup` for every grid. Only tooltips move; menus and pickers
 * keep AG Grid's placement. The card sits above the hovered cell, centred on
 * the pointer (on the cell for a keyboard focus), and drops below the cell
 * only when the window has no room above it.
 */
export function placeGridTooltip({ type, ePopup, eventSource, mouseEvent }) {
  if (type !== 'tooltip') return;
  const hovered = eventSource ?? mouseEvent.target;
  const anchor = (hovered.closest(ANCHOR) ?? hovered).getBoundingClientRect();
  const card = ePopup.getBoundingClientRect();
  // style.left/top count from the popup parent; the rects from the window.
  const originX = card.left - parseFloat(ePopup.style.left);
  const originY = card.top - parseFloat(ePopup.style.top);

  const pointX = mouseEvent ? mouseEvent.clientX : anchor.left + anchor.width / 2;
  const viewportWidth = document.documentElement.clientWidth;
  const left = clamp(pointX - card.width / 2, EDGE_PX, viewportWidth - card.width - EDGE_PX);
  const above = anchor.top - GAP_PX - card.height;
  const placement = above >= EDGE_PX ? 'top' : 'bottom';
  const top = placement === 'top' ? above : anchor.bottom + GAP_PX;

  ePopup.style.left = `${left - originX}px`;
  ePopup.style.top = `${top - originY}px`;
  ePopup.dataset.tooltipPlacement = placement;
  ePopup.style.setProperty(
    '--tooltip-arrow-x',
    `${clamp(pointX - left, ARROW_INSET_PX, card.width - ARROW_INSET_PX)}px`,
  );
}

export const GRID_TOOLTIP_OPTIONS = {
  tooltipShowDelay: TOOLTIP_SHOW_DELAY_MS,
  postProcessPopup: placeGridTooltip,
};
